import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import PerfectScrollbar from 'react-perfect-scrollbar'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { reverse } from 'named-urls'
import { Box, Typography, Button, Chip } from '@material-ui/core'

import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from 'components/Table'
import DeleteFormModal from '../DeleteFormModal/DeleteFormModal'
import InactiveFormModal from '../InactiveFormModal/InactiveFormModal'
import { MenuButton, Permitted } from 'components'
import useFetch from 'hooks/useFetch'

import helpers from 'helpers'

import styles from './styles'
import * as service from 'service'
import { routes } from 'Routes'
import constants from 'constants/index'
import permissions from 'constants/permissions'

const ConsentTableRow = ({ consent, refresh = () => {}, ...rest }) => {
  const {
    ACTIVE_STATUS_ID,
    CONSENT_FORM_STATUS_LABEL_TRANSLATION,
    INACTIVE_STATUS_ID,
    EXPIRED_STATUS_ID,
  } = constants.consent

  const history = useHistory()
  const classes = styles()
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openInactiveModal, setOpenInactiveModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const isActive =
    consent?.status === constants.consent.ACTIVE_CONSENT_FORM_STATUS_ID

  const handleOpenDeleteModal = () => setOpenDeleteModal(true)
  const handleOpenInactiveModal = () => setOpenInactiveModal(true)

  const handleRedirectToShow = (consentFormId) => {
    history.push(
      reverse(routes.consent.show, {
        consentFormId,
      }),
    )
  }

  const handleRedirectToEdit = (consentFormId) => {
    history.push(
      reverse(routes.consent.edit, {
        consentFormId,
      }),
    )
  }

  return (
    <>
      <TableRow {...rest}>
        <TableCell>
          <Typography className={classes.rowText}>{consent?.id}</Typography>
        </TableCell>
        <TableCell>
          <Typography className={classes.rowText}>{consent?.title}</Typography>
        </TableCell>
        <TableCell align="center">
          <Typography className={classes.rowText}>
            {helpers.formatters.date(consent?.updatedAt) || '-'}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography>{consent?.author?.name}</Typography>
        </TableCell>
        <TableCell align="center">
          <Chip
            label={
              CONSENT_FORM_STATUS_LABEL_TRANSLATION[consent?.status] ||
              'Indefinido'
            }
            size="small"
            className={clsx(classes.chip, {
              [classes.activeChip]: consent?.status === ACTIVE_STATUS_ID,
              [classes.inactiveChip]: consent?.status === INACTIVE_STATUS_ID,
              [classes.expiredChip]: consent?.status === EXPIRED_STATUS_ID,
            })}
          />
        </TableCell>
        <TableCell align="center">
          <Typography className={classes.rowText}>
            {(consent?.answersCollected?.authorized ?? '0') +
              '/' +
              (consent?.answersCollected?.revoked ?? '0')}
          </Typography>
        </TableCell>
        <Permitted
          tag={[permissions.CONSENTS.SHOW, permissions.CHANGE_RESPONSIBLE]}
        >
          <TableCell align="right">
            <MenuButton>
              <Permitted tag={permissions.CONSENTS.SHOW}>
                <Button
                  color="secondary"
                  onClick={() => handleRedirectToShow(consent?.id)}
                  fullWidth
                  size="small"
                >
                  Ver detalhes
                </Button>
              </Permitted>
              <Permitted tag={permissions.CONSENTS.CHANGE_RESPONSIBLE}>
                <Button
                  color="secondary"
                  onClick={() => handleRedirectToEdit(consent?.id)}
                  fullWidth
                  size="small"
                >
                  Editar
                </Button>
              </Permitted>
              <Permitted
                tag={constants.permissions.CONSENTS.CHANGE_FORM_STATUS}
              >
                <Button
                  onClick={handleOpenInactiveModal}
                  color="secondary"
                  fullWidth
                  size="small"
                >
                  {isActive ? 'Inativar' : 'Ativar'} Formulário
                </Button>
              </Permitted>

              <Permitted tag={constants.permissions.CONSENTS.DESTROY}>
                <Button
                  onClick={handleOpenDeleteModal}
                  handleOpenDeleteModal
                  color="secondary"
                  fullWidth
                  size="small"
                >
                  Excluir formulário
                </Button>
              </Permitted>
            </MenuButton>
          </TableCell>
        </Permitted>
      </TableRow>
      <InactiveFormModal
        consentForm={consent}
        open={openInactiveModal}
        setOpen={setOpenInactiveModal}
        loading={loading}
        setLoading={setLoading}
        refresh={refresh}
        isActive={isActive}
      />
      <DeleteFormModal
        consentForm={consent}
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        loading={loading}
        setLoading={setLoading}
        refresh={refresh}
      />
    </>
  )
}

const ConsentTable = ({ consents, refresh = () => {} }) => {
  const classes = styles()

  return (
    <>
      <Box width="100%">
        <PerfectScrollbar>
          <Table
            size="small"
            emptyMessage="Nenhum formulário encontrado"
            noWrap
          >
            <TableHead>
              <TableRow>
                <TableCell width="10%">
                  <Box my={1}>
                    <Typography className={classes.headText}>ID</Typography>
                  </Box>
                </TableCell>
                <TableCell width="15%">
                  <Typography className={classes.headText}>Título</Typography>
                </TableCell>
                <TableCell width="15%" align="center">
                  <Typography className={classes.headText}>
                    Última Atualização
                  </Typography>
                </TableCell>
                <TableCell width="15%">
                  <Typography className={classes.headText}>
                    Criado por
                  </Typography>
                </TableCell>
                <TableCell width="15%" align="center">
                  <Typography className={classes.headText}>Status</Typography>
                </TableCell>
                <TableCell width="15%" align="center">
                  <Typography className={classes.headText}>
                    Coletados/Revogados
                  </Typography>
                </TableCell>
                <Permitted
                  tag={[
                    permissions.CONSENTS.SHOW,
                    permissions.CONSENTS.CHANGE_RESPONSIBLE,
                  ]}
                >
                  <TableCell align="right">
                    <Typography className={classes.headText}>Ações</Typography>
                  </TableCell>
                </Permitted>
              </TableRow>
            </TableHead>
            <TableBody>
              {consents?.map((consent) => (
                <ConsentTableRow
                  consent={consent}
                  key={consent?.id}
                  refresh={refresh}
                />
              ))}
            </TableBody>
          </Table>
        </PerfectScrollbar>
      </Box>
    </>
  )
}

ConsentTableRow.propTypes = {
  consent: PropTypes.object,
  refresh: PropTypes.func,
}

ConsentTable.propTypes = {
  consents: PropTypes.array,
  refresh: PropTypes.func,
}

export default ConsentTable
